import {useEffect, useMemo, useState} from 'react'
import type {SyncRun} from '../api'
import {
  estimateSyncDuration,
  estimatedSyncProgress,
  persistedSyncEstimate,
} from '../utils/syncProgress'

export interface ManualSyncAttempt {
  startedAt: number
  finishedAt?: number
  outcome: 'RUNNING' | 'OK' | 'ERRO'
}

interface Props {
  running: boolean
  latest?: SyncRun
  runs: SyncRun[]
  manualAttempt: ManualSyncAttempt | null
}

interface ProgressSession {
  active: boolean
  persisted: boolean
  runId: number | null
  manualStart: number | null
  observedManualStart: number | null
  startedAt: number
  durationMs: number
  sampleCount: number
}

export default function SyncProgress({running, latest, runs, manualAttempt}: Props) {
  const storedEstimate = useMemo(
    () => (latest?.status === 'RUNNING' ? persistedSyncEstimate(latest) : null),
    [latest]
  )
  const hasPersistedEstimate = storedEstimate !== null
  const estimate = useMemo(
    () => storedEstimate ?? estimateSyncDuration(runs),
    [storedEstimate, runs]
  )
  const [session, setSession] = useState<ProgressSession | null>(null)
  const [now, setNow] = useState(Date.now)
  const activeRunId = latest?.status === 'RUNNING' ? latest.id : null
  const activeStart = activeRunId !== null ? Date.parse(latest!.iniciado_em) : NaN
  const manualStart = manualAttempt?.startedAt ?? null
  const manualRunning = manualAttempt?.outcome === 'RUNNING'

  useEffect(() => {
    if (!running) {
      setSession((previous) => (previous?.active ? {...previous, active: false} : previous))
      return
    }
    setSession((previous) => {
      const newManualAttempt = manualRunning && manualStart !== previous?.observedManualStart
      const newServerRun =
        activeRunId !== null && previous?.runId != null && activeRunId !== previous.runId
      if (!previous || !previous.active || newManualAttempt || newServerRun) {
        return {
          ...estimate,
          active: true,
          persisted: hasPersistedEstimate,
          runId: activeRunId,
          manualStart: manualRunning && !newServerRun ? manualStart : null,
          observedManualStart: manualStart,
          startedAt: Number.isFinite(activeStart) ? activeStart : manualStart ?? Date.now(),
        }
      }
      // Substitui a estimativa provisória pela mesma duração e início que serão
      // recuperados do banco ao reabrir a tela ou atualizar a página.
      if (
        activeRunId !== null &&
        (previous.runId === null || (hasPersistedEstimate && !previous.persisted))
      ) {
        return {
          ...previous,
          ...estimate,
          persisted: hasPersistedEstimate,
          runId: activeRunId,
          startedAt: Number.isFinite(activeStart) ? activeStart : previous.startedAt,
        }
      }
      return previous
    })
  }, [
    running,
    activeRunId,
    activeStart,
    manualStart,
    manualRunning,
    estimate,
    hasPersistedEstimate,
  ])

  useEffect(() => {
    if (!running) return
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [running])

  if (!session) return null
  const matchingRun = session.runId !== null && latest?.id === session.runId
  if (matchingRun && latest?.status === 'ERRO') return null
  const serverCompleted = matchingRun && latest?.status === 'OK'
  const manualCompleted =
    session.manualStart !== null &&
    manualAttempt?.startedAt === session.manualStart &&
    manualAttempt.outcome === 'OK'
  const completed =
    matchingRun && latest?.status !== 'RUNNING'
      ? serverCompleted
      : manualCompleted || serverCompleted
  if (!running && !completed) return null

  const finishedAt = serverCompleted ? Date.parse(latest!.fim_em ?? '') : manualAttempt?.finishedAt
  const end =
    completed && finishedAt !== undefined && Number.isFinite(finishedAt) ? finishedAt : now
  const elapsedMs = Math.max(0, end - session.startedAt)
  const progress = completed ? 100 : estimatedSyncProgress(elapsedMs, session.durationMs)
  const percent = Math.floor(progress)
  const overdue = !completed && elapsedMs >= session.durationMs
  const note = completed
    ? 'Dados atualizados com sucesso.'
    : overdue
    ? 'A sincronização está levando mais tempo que o estimado. Aguardando a conclusão.'
    : 'Os dados continuam disponíveis durante a atualização.'

  return (
    <div className={`sync-progress${completed ? ' is-complete' : ''}`}>
      <div className='sync-progress-heading'>
        <strong>{completed ? 'Sincronização concluída' : 'Progresso estimado'}</strong>
        <span>{percent}%</span>
      </div>
      <div
        className='sync-progress-track'
        role='progressbar'
        aria-label='Progresso estimado da sincronização'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={completed ? '100%, sincronização concluída' : `${percent}% estimado`}
        aria-describedby='sync-progress-note'
      >
        <div className='sync-progress-fill' style={{transform: `scaleX(${progress / 100})`}} />
      </div>
      <p id='sync-progress-note' className='sync-progress-note' aria-live='polite'>
        {note}
      </p>
      <p className='sync-progress-history'>
        {session.sampleCount
          ? `Estimativa baseada ${
              session.sampleCount === 1
                ? 'na última sincronização concluída'
                : `nas últimas ${session.sampleCount} sincronizações concluídas`
            }.`
          : 'Estimativa inicial; ainda não há histórico de duração disponível.'}
      </p>
    </div>
  )
}
